import { SensorType, useSensorAlerts, API } from 'common';
import {
  requestWaterLeakService,
  updateSensorAlertAcknowledgedStateAnonymous,
} from 'common/src/graphql/mutations';
import { getSensorAlert, getServiceRequest } from 'common/src/graphql/queries';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSync } from './AppSyncContext';

export enum WaterAlertState {
  NoAlert = 'NoAlert',
  Alert = 'Alert',
  ServiceRequested = 'ServiceRequested',
  ServiceScheduled = 'ServiceScheduled',
  InProgress = 'InProgress',
}

interface WaterAlertContextType {
  alertState: WaterAlertState;
  alertMessage: string;
  onRequestService: () => Promise<void>;
  onClearAlert: () => Promise<void>;
  loading: boolean;
}

const WaterAlertContext = createContext<WaterAlertContextType | undefined>(undefined);

export const WaterAlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { sensors } = useAppSync();

  const leakSensor = sensors.find((sensor) => sensor.type === SensorType.LEAK_DETECTOR);

  const {
    sensorAlerts,
    fetchAndUpdateSensorAlerts,
    loading: alertsLoading,
  } = useSensorAlerts(leakSensor?.id);

  const [alertState, setAlertState] = useState<WaterAlertState>(WaterAlertState.NoAlert);
  const [alertMessage, setAlertMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeAlert, setActiveAlert] = useState<any>(null);

  const determineAlertStateFromServiceRequest = async (serviceRequestId: string) => {
    try {
      const serviceResponse: any = await API.graphql({
        query: getServiceRequest,
        variables: { id: serviceRequestId },
      });

      if (serviceResponse?.data?.getServiceRequest) {
        const serviceRequest = serviceResponse.data.getServiceRequest;
        const status = serviceRequest.status?.toLowerCase();

        switch (status) {
          case 'in_progress':
            return {
              state: WaterAlertState.InProgress,
              message: t('water.serviceInProgress'),
            };
          case 'open':
          case 'scheduled':
            return {
              state: WaterAlertState.ServiceScheduled,
              message: t('water.serviceScheduled'),
            };
          case 'pending':
          case 'requested':
            return {
              state: WaterAlertState.ServiceRequested,
              message: t('water.serviceRequested'),
            };
          case 'closed':
          case 'canceled':
            // When service request is closed or canceled, return null to indicate
            // we should not use service request state and let processAlerts determine
            // the appropriate state based on alert acknowledgment
            return null;
          default:
            return {
              state: WaterAlertState.ServiceRequested,
              message: t('water.serviceRequested'),
            };
        }
      }
    } catch (error) {
      console.error('Failed to fetch service request details:', error);
    }

    return {
      state: WaterAlertState.ServiceRequested,
      message: t('water.serviceRequested'),
    };
  };

  useEffect(() => {
    if (leakSensor?.id) {
      fetchAndUpdateSensorAlerts(leakSensor.id);
    }
  }, [leakSensor?.id]);

  useEffect(() => {
    const processAlerts = async () => {
      if (alertsLoading || sensors.length === 0) return;

      const now = Date.now();
      const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;

      const alert = sensorAlerts.find((alert) => {
        const isLeakAlert = alert.alertType.includes('LEAK');
        const isActiveStatus = alert.status === 'new' || alert.status === 'ongoing';
        const isUnacknowledged = alert.acknowledgedAt === null && alert.acknowledgedBy === null;
        const isRecent = alert.timestamp && alert.timestamp * 1000 >= twentyFourHoursAgo;

        return isLeakAlert && isActiveStatus && isUnacknowledged && isRecent;
      });

      if (alert) {
        try {
          const alertResponse: any = await API.graphql({
            query: getSensorAlert,
            variables: { id: alert.id },
          });

          if (alertResponse?.data?.getSensorAlert) {
            const fullAlert = alertResponse.data.getSensorAlert;
            setActiveAlert(fullAlert);

            const serviceRequest = fullAlert.serviceRequest;
            if (serviceRequest && serviceRequest.id) {
              const serviceRequestState = await determineAlertStateFromServiceRequest(
                serviceRequest.id,
              );
              if (serviceRequestState) {
                setAlertState(serviceRequestState.state);
                setAlertMessage(serviceRequestState.message);
              } else {
                // Service request is closed/canceled
                // Check if the alert is acknowledged (should be auto-acknowledged when service is closed)
                if (fullAlert.acknowledgedAt && fullAlert.acknowledgedBy) {
                  // Alert is acknowledged, no need to show anything
                  setAlertState(WaterAlertState.NoAlert);
                  setAlertMessage('');
                } else {
                  // Alert is not acknowledged for some reason, show as normal alert
                  setAlertState(WaterAlertState.Alert);
                  setAlertMessage(fullAlert.message || t('water.unexpectedLeakDetected'));
                }
              }
            } else {
              setAlertState(WaterAlertState.Alert);
              setAlertMessage(fullAlert.message || t('water.unexpectedLeakDetected'));
            }
          } else {
            setActiveAlert(alert);
            setAlertState(WaterAlertState.Alert);
            setAlertMessage(alert.message || t('water.unexpectedLeakDetected'));
          }
        } catch (error) {
          console.error('Failed to fetch full alert details:', error);
          setActiveAlert(alert);
          setAlertState(WaterAlertState.Alert);
          setAlertMessage(alert.message || t('water.unexpectedLeakDetected'));
        }
      } else {
        setActiveAlert(null);
        setAlertState(WaterAlertState.NoAlert);
        setAlertMessage('');
      }
    };

    processAlerts();
  }, [sensorAlerts.length, alertsLoading, sensors.length, sensorAlerts, sensors, t]);

  const handleRequestService = async () => {
    if (
      alertState === WaterAlertState.ServiceRequested ||
      alertState === WaterAlertState.ServiceScheduled ||
      alertState === WaterAlertState.InProgress
    ) {
      console.log('Service already requested, scheduled, or in progress for this alert');
      return;
    }

    if (activeAlert?.serviceRequest) {
      console.log('Alert already has an associated service request');
      return;
    }

    setLoading(true);
    try {
      if (activeAlert) {
        const alertSensor = sensors.find((s) => s.id === activeAlert.sensorId);
        if (alertSensor) {
          await API.graphql({
            query: requestWaterLeakService,
            variables: {
              input: {
                hubId: alertSensor.hubId,
                sensorId: alertSensor.id,
                alertId: activeAlert.id,
                priority: 'Critical',
              },
            },
          });

          try {
            const alertResponse: any = await API.graphql({
              query: getSensorAlert,
              variables: { id: activeAlert.id },
            });

            if (alertResponse?.data?.getSensorAlert) {
              const updatedAlert = alertResponse.data.getSensorAlert;
              setActiveAlert(updatedAlert);

              const serviceRequest = updatedAlert.serviceRequest;
              if (serviceRequest && serviceRequest.id) {
                const serviceRequestState = await determineAlertStateFromServiceRequest(
                  serviceRequest.id,
                );
                if (serviceRequestState) {
                  setAlertState(serviceRequestState.state);
                  setAlertMessage(serviceRequestState.message);
                } else {
                  // Service request is closed/canceled, set to requested state for newly created service
                  setAlertState(WaterAlertState.ServiceRequested);
                  setAlertMessage(t('water.serviceRequested'));
                }
              } else {
                setAlertState(WaterAlertState.ServiceRequested);
                setAlertMessage(t('water.serviceRequested'));
              }
            }
          } catch (alertFetchError) {
            console.error('Failed to fetch updated alert details:', alertFetchError);
            setAlertState(WaterAlertState.ServiceRequested);
            setAlertMessage(t('water.serviceRequested'));
          }

          if (leakSensor?.id) fetchAndUpdateSensorAlerts(leakSensor.id);
        }
      }
    } catch (error) {
      console.error('Failed to request service:', error);
      setAlertMessage(t('water.failedToRequestService'));
    } finally {
      setLoading(false);
    }
  };

  const handleClearAlert = async () => {
    setLoading(true);
    try {
      if (activeAlert) {
        const response: any = await API.graphql({
          query: updateSensorAlertAcknowledgedStateAnonymous,
          variables: {
            input: {
              id: activeAlert.id,
              isAcknowledged: true,
            },
          },
        });

        if (response?.data?.updateSensorAlertAcknowledgedStateAnonymous) {
          setAlertState(WaterAlertState.NoAlert);
          setAlertMessage('');
          setActiveAlert(null);
          if (leakSensor?.id) await fetchAndUpdateSensorAlerts(leakSensor.id);
        } else {
          console.error('Failed to acknowledge alert - no data returned');
          setAlertMessage(t('water.failedToClearAlert'));
        }
      }
    } catch (error) {
      console.error('Failed to clear alert:', error);
      setAlertMessage(t('water.failedToClearAlert'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <WaterAlertContext.Provider
      value={{
        alertState,
        alertMessage,
        onRequestService: handleRequestService,
        onClearAlert: handleClearAlert,
        loading: loading || alertsLoading,
      }}>
      {children}
    </WaterAlertContext.Provider>
  );
};

export const useWaterAlertContext = (): WaterAlertContextType => {
  const context = useContext(WaterAlertContext);
  if (!context) {
    throw new Error('useWaterAlertContext must be used within a WaterAlertProvider');
  }
  return context;
};
